// src/paginas/SolicitarRecuperacion.js
// Primer paso del flujo de "Recuperación de contraseña": el usuario introduce su correo
// y, si corresponde a una cuenta activa, recibe un email con el enlace para restablecerla.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../servicios/api';

const SolicitarRecuperacion = () => {
  const [email, setEmail] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [tipoMensaje, setTipoMensaje] = useState('');
  const [enviado, setEnviado] = useState(false);

  const manejarEnvio = async (e) => {
    e.preventDefault();
    setMensaje('');
    setEnviando(true);

    try {
      const response = await fetch(`${API_URL}/solicitar_recuperacion.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const datos = await response.json();
      setTipoMensaje(datos.exito ? 'exito' : 'error');
      setMensaje(datos.mensaje);
      if (datos.exito) {
        setEnviado(true);
      }
    } catch (error) {
      console.error('Error al solicitar recuperación:', error);
      setTipoMensaje('error');
      setMensaje('Error de conexión con el servidor. Inténtalo de nuevo más tarde.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-dark-background px-4 py-10">
      <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-brand-orange/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-sky-500/10 blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="rounded-2xl border border-white/10 bg-gray-900/80 p-8 shadow-2xl shadow-black/40 backdrop-blur-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-orange/15 ring-1 ring-brand-orange/30">
            <svg viewBox="0 0 24 24" fill="none" className="h-7 w-7 text-brand-orange" stroke="currentColor" strokeWidth="1.8">
              <rect x="5" y="11" width="14" height="9" rx="2" strokeLinecap="round" strokeLinejoin="round" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V8a4 4 0 0 1 8 0v3" />
              <path strokeLinecap="round" strokeLinejoin="round" d="m14.5 15.5 2 2 3.5-3.5" />
            </svg>
          </div>
          <h3 className="mt-4 text-center text-2xl font-bold tracking-tight text-white">¿Olvidaste tu contraseña?</h3>
          <p className="mt-1 text-center text-sm leading-relaxed text-gray-400">
            Indica tu correo y, si tienes una cuenta activa, te enviaremos un enlace para restablecerla.
          </p>

          {mensaje && (
            <div
              role="alert"
              className={`mt-5 flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm ${
                tipoMensaje === 'exito'
                  ? 'border-emerald-800 bg-emerald-950/40 text-emerald-200'
                  : 'border-red-800 bg-red-950/40 text-red-200'
              }`}
            >
              <svg viewBox="0 0 24 24" fill="none" className="mt-0.5 h-4 w-4 flex-shrink-0" stroke="currentColor" strokeWidth="2">
                {tipoMensaje === 'exito' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.3 3.86 1.8 18a1 1 0 0 0 .86 1.5h18.68a1 1 0 0 0 .86-1.5L13.7 3.86a1 1 0 0 0-1.7 0Z" />
                )}
              </svg>
              <span>{mensaje}</span>
            </div>
          )}

          {!enviado && (
            <form onSubmit={manejarEnvio} className="mt-6 space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-400" htmlFor="email">
                  Correo electrónico
                </label>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                    <svg viewBox="0 0 24 24" fill="none" className="h-[18px] w-[18px]" stroke="currentColor" strokeWidth="1.8">
                      <rect x="3" y="5" width="18" height="14" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="m4 7 8 6 8-6" />
                    </svg>
                  </span>
                  <input
                    type="email"
                    placeholder="tucorreo@ejemplo.com"
                    className="w-full rounded-lg border border-gray-700 bg-gray-800/80 py-2.5 pl-10 pr-3 text-sm text-white placeholder:text-gray-500 transition focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={enviando}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-orange py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-orange/20 transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {enviando && (
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.4 0 0 5.4 0 12h4Z" />
                  </svg>
                )}
                {enviando ? 'Enviando...' : 'Enviar enlace de recuperación'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center text-sm text-gray-400">
            <Link to="/iniciar-sesion" className="font-medium text-brand-orange hover:underline">
              Volver a iniciar sesión
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SolicitarRecuperacion;
